import { Button } from "@progress/kendo-react-buttons";
import { Badge, BadgeContainer } from "@progress/kendo-react-indicators";
import { Popup } from "@progress/kendo-react-popup";
import React, { useEffect, useRef, useState } from "react";
import {
	getAlertsApi,
	getUserDefaultOrganizationApi,
	HeaderFilter,
	saveUserDefaultOrganizationApi,
} from "api/user";
import { getVesselFilter } from "api/vessel";
import { useClickOutside, useEscapeKey } from "hooks/hooks";
import { AlertMessage } from "interface/alert";
import { OrganizationType, UserAccessOrganization } from "interface/user";
import { useTranslation } from "react-i18next";
import { useUserOrganizationsStore, useUserStore } from "store";
import CustomDropDownList from "ui/dropDown";
import { VesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import { BuyerRenderItem } from "ui/dropdownItemList/BuyerRenderItems";
import { ICON_NAMES } from "utils/constant";
import UserOrganization from "./HomePageItem";
import Notification from "./Notification";
import UserSetting from "./userprofile/UserSetting";
import { Vessel } from "interface/vessel";
import { Buyer } from "interface/buyer";
import { getBuyersFilterApi, getTerminalsApi } from "api/common";
import { DropDownItem } from "interface/common";
import { DropdownRenderItem } from "ui/dropdownItemList";
import { CustomButton } from "ui/Button";
import { COLOR } from "utils/constant";
import CustomModal from "ui/modal/Modal";

const HeaderItem: React.FC = () => {
	const [showSetting, setShowSetting] = useState(false);
	const [showOrganizations, setShowOrganizations] = useState(false);
	const [showNotification, setShowNotification] = useState(false);
	const [showAdminFilterModal, setShowAdminFilterModal] = useState(false);
	const { defaultOrg, setDefaultOrganization } = useUserOrganizationsStore();
	const { isAdmin } = useUserStore();
	const [notifications, setNotifications] = useState<AlertMessage[]>([]);

	const orgRef = useRef<HTMLDivElement>(null);
	const orgAnchorRef = useRef<HTMLDivElement>(null);
	const notificationRef = useRef<HTMLDivElement>(null);
	const notificationAnchorRef = useRef<HTMLDivElement>(null);
	const settingRef = useRef<HTMLDivElement>(null);
	const settingAnchorRef = useRef<HTMLDivElement>(null);

	const [filterOptions, setFilterOptions] = useState<Partial<HeaderFilter>>({});

	const { t } = useTranslation();

	useEffect(() => {
		getUserDefaultOrganizationApi()
			.then((response) => {
				setDefaultOrganization(response.data);
			})
			.catch((err) => {
				console.log(err);
			});
	}, [setDefaultOrganization]);

	useEffect(() => {
		if (!defaultOrg) return;

		let parmas: any = {};

		if (defaultOrg.type === OrganizationType.Terminal) {
			parmas.terminalId = defaultOrg.id;

			if (isAdmin) {
				const terminal: DropDownItem = {
					code: defaultOrg.id,
					name: defaultOrg.displayName,
					displayName: defaultOrg.displayName,
				};
				handleTerminal(terminal, false);
			}
		} else if (defaultOrg.type === OrganizationType.Buyer) {
			parmas.buyerLedgerAccount = defaultOrg.ledgerAccountNumber;

			if (isAdmin) {
				const buyer = {
					id: defaultOrg.ledgerAccountNumber,
					name: defaultOrg.displayName,
					orgNo: defaultOrg.businessNumber,
				} as Buyer;
				handleBuyer(buyer, false);
			}
		} else if (defaultOrg.type === OrganizationType.Vessel) {
			parmas.vesselLedgerAccount = defaultOrg.ledgerAccountNumber;

			if (isAdmin) {
				const vessel = {
					id: defaultOrg.vesselId,
					nm: defaultOrg.displayName,
					res: defaultOrg.ledgerAccountNumber
						? Number(defaultOrg.ledgerAccountNumber)
						: 0,
				} as Vessel;
				handleVessel(vessel, false);
			}
		}

		getAlertsApi({
			...parmas,
		})
			.then((response) => {
				setNotifications(response.data);
			})
			.catch((error) => {
				console.log(error);
			});
	}, [defaultOrg]);

	useClickOutside(orgRef, (node) => {
		if (node.classList.contains("k-panelbar-item-text")) {
			return;
		}
		setShowOrganizations(false);
	});
	useClickOutside(notificationRef, () => {
		setShowNotification(false);
	});
	useClickOutside(settingRef, (node) => {
		if (node.classList.contains("k-panelbar-item-text")) {
			return;
		}
		setShowSetting(false);
	});

	useEscapeKey(() => {
		setShowSetting(false);
		setShowOrganizations(false);
		setShowNotification(false);
		setShowAdminFilterModal(false);
	});

	const getIcon = (type: OrganizationType) => {
		if (type === OrganizationType.Buyer) {
			return ICON_NAMES.Buyer;
		}
		if (type === OrganizationType.Vessel) {
			return ICON_NAMES.Vessel;
		}
		if (type === OrganizationType.Terminal) {
			return ICON_NAMES.Terminal;
		}
	};

	const toggleShowOrg = () => {
		setShowOrganizations(!showOrganizations);
		setShowNotification(false);
		setShowSetting(false);
	};

	const toggleNotification = () => {
		setShowNotification(!showNotification);
		setShowOrganizations(false);
		setShowSetting(false);
	};

	const handleDefaultOrgClick = () => {
		if (isAdmin) {
			setShowAdminFilterModal(true);
			setShowNotification(false);
			setShowSetting(false);
		} else {
			toggleShowOrg();
		}
	};

	const handleVessel = (vessel: Vessel, save: boolean = true) => {
		setFilterOptions({
			...filterOptions,
			vesselHullId: vessel.id as any,
			vesselId: vessel.id,
			vesselName: vessel.id ? vessel.nm : "",
			vesselLedgerAccountNumber: vessel.res?.toString(),
			terminalCode: undefined,
			terminalName: undefined,
			buyerName: undefined,
			buyerLedgerAccountNumber: undefined,
		});

		if (save) {
			const updatedOrg: UserAccessOrganization = {
				...defaultOrg,
				businessNumber: "",
				displayName: `${vessel.nm}  ${vessel.rn}`,
				ledgerAccountNumber: vessel.res?.toString(),
				buyerName: "",
				terminalName: "",
				vesselRegistrationNumber: vessel.rn,
				vesselId: vessel.id.toString(),
				type: OrganizationType.Vessel,
				id: "",
			};
			setDefaultOrganization(updatedOrg);
			saveUserDefaultOrganizationApi(updatedOrg)
				.then((_response) => {})
				.catch((err) => {
					console.log(err);
				});
			setShowAdminFilterModal(false);
		}
	};

	const handleBuyer = (buyer: Buyer, save: boolean = true) => {
		setFilterOptions({
			...filterOptions,
			buyerName: buyer.name,
			buyerLedgerAccountNumber: buyer.id,
			vesselHullId: undefined,
			vesselId: undefined,
			vesselName: undefined,
			vesselLedgerAccountNumber: undefined,
			terminalCode: undefined,
			terminalName: undefined,
		});

		if (save) {
			const updatedOrg: UserAccessOrganization = {
				...defaultOrg,
				businessNumber: buyer.orgNo ? buyer.orgNo : "",
				displayName: buyer.name,
				ledgerAccountNumber: buyer.id?.toString(),
				buyerName: "",
				terminalName: "",
				vesselRegistrationNumber: "",
				vesselId: "",
				type: OrganizationType.Buyer,
				id: "",
			};
			setDefaultOrganization(updatedOrg);
			saveUserDefaultOrganizationApi(updatedOrg)
				.then((_response) => {})
				.catch((err) => {
					console.log(err);
				});
			setShowAdminFilterModal(false);
		}
	};

	const handleTerminal = (terminal: DropDownItem, save: boolean = true) => {
		setFilterOptions({
			...filterOptions,
			terminalCode: terminal.code,
			terminalName: terminal.name,
			vesselHullId: undefined,
			vesselId: undefined,
			vesselName: undefined,
			vesselLedgerAccountNumber: undefined,
			buyerName: undefined,
			buyerLedgerAccountNumber: undefined,
		});

		if (save) {
			const updatedOrg: UserAccessOrganization = {
				...defaultOrg,
				businessNumber: "",
				displayName: terminal.name,
				buyerName: "",
				terminalName: "",
				vesselRegistrationNumber: "",
				vesselId: "",
				type: OrganizationType.Terminal,
				id: terminal.code,
			};
			setDefaultOrganization(updatedOrg);
			saveUserDefaultOrganizationApi(updatedOrg)
				.then((_response) => {})
				.catch((err) => {
					console.log(err);
				});
			setShowAdminFilterModal(false);
		}
	};

	const handleClearFilter = () => {
		setFilterOptions({});

		setDefaultOrganization(null);

		saveUserDefaultOrganizationApi(null)
			.then((_response) => {})
			.catch((err) => {
				console.log(err);
			});

		setShowAdminFilterModal(false);
	};

	return (
		<div
			className="app-header-row"
			style={{
				display: "flex",
				height: "44px",
				paddingRight: 30,
				justifyContent: "space-between",
			}}
		>
			<div>
				{(defaultOrg || isAdmin) && (
					<div ref={orgRef} className="header-popover-anchor">
						<div ref={orgAnchorRef}>
							<Button
								style={{
									backgroundColor: "transparent",
									borderColor: "transparent",
									fontFamily: "Work Sans",
									fontSize: "15px",
									lineHeight: "18px",
									borderRadius: "12px",
									marginLeft: "-4px",
								}}
								startIcon={
									<img
										src={
											defaultOrg
												? `/icon/${getIcon(defaultOrg.type)}.svg`
												: "/icon/administration.svg"
										}
										alt={
											defaultOrg ? getIcon(defaultOrg.type) : "administration"
										}
										style={{}}
									/>
								}
								className="org-name"
								onClick={handleDefaultOrgClick}
							>
								{defaultOrg ? defaultOrg.displayName : "Velg organisasjon"}
							</Button>
						</div>
						{/* Non-admin org popup */}
						{!isAdmin && (
							<Popup
								anchor={orgAnchorRef.current}
								appendTo={orgRef.current}
								anchorAlign={{
									horizontal: "left",
									vertical: "bottom",
								}}
								popupAlign={{
									horizontal: "left",
									vertical: "top",
								}}
								show={showOrganizations}
								popupClass={"org-name-popup"}
								style={{
									borderRadius: "12px",
									background: "none",
								}}
							>
								<UserOrganization
									onSelect={() => setShowOrganizations(false)}
								/>
							</Popup>
						)}
					</div>
				)}
			</div>

			{/* Admin filter modal */}
			{isAdmin && showAdminFilterModal && (
				<CustomModal
					title={t("ChangeOrganization")}
					onClose={() => setShowAdminFilterModal(false)}
					style={{ width: "min(720px, calc(100vw - 24px))" }}
				>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "16px",
							alignItems: "flex-end",
						}}
					>
						<div style={{ minWidth: "180px", flex: 1 }}>
							<CustomDropDownList
								data={[]}
								filterable={true}
								dataItemKey="id"
								textField="nm"
								label={t("Vessel")}
								value={{
									id: filterOptions.vesselId || null,
									nm: filterOptions.vesselName || " ",
								}}
								defaultItem={{ id: null, nm: t("All") }}
								onSelect={handleVessel}
								serverFilter={true}
								serverFilterApi={getVesselFilter}
								itemRender={VesselRenderItem()}
								popupSettings={{ width: 690 }}
							/>
						</div>

						<div style={{ minWidth: "180px", flex: 1 }}>
							<CustomDropDownList
								data={[]}
								filterable={true}
								dataItemKey="id"
								textField="name"
								label={t("Terminal")}
								value={{
									id: filterOptions.terminalCode || null,
									name: filterOptions.terminalName || " ",
								}}
								defaultItem={{ id: null, name: t("All") }}
								onSelect={handleTerminal}
								serverFilter={true}
								serverFilterApi={getTerminalsApi}
								itemRender={DropdownRenderItem}
								popupSettings={{ width: 450 }}
							/>
						</div>

						<div style={{ minWidth: "180px", flex: 1 }}>
							<CustomDropDownList
								data={[]}
								filterable={true}
								dataItemKey="id"
								textField="name"
								label={t("Buyer")}
								value={{
									id: filterOptions.buyerLedgerAccountNumber || null,
									name: filterOptions.buyerName || " ",
								}}
								defaultItem={{ id: null, name: t("All") }}
								onSelect={handleBuyer}
								serverFilter={true}
								serverFilterApi={getBuyersFilterApi}
								itemRender={BuyerRenderItem()}
								popupSettings={{ width: 450 }}
							/>
						</div>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-end",
							}}
						>
							<CustomButton
								label={t("Clear")}
								onClick={handleClearFilter}
								bgColor={COLOR.Green}
								color="white"
								btnStyle={{ height: "32px" }}
							/>
						</div>
					</div>
				</CustomModal>
			)}

			<div style={{ display: "flex", gap: 1 }}>
				<div ref={notificationRef} className="header-popover-anchor">
					<div ref={notificationAnchorRef}>
						<Button
							style={{
								backgroundColor: "transparent",
								borderColor: "transparent",
								fontFamily: "Work Sans",
								lineHeight: "18px",
								borderRadius: "12px",
								marginRight: "0px",
							}}
							disabled={notifications.length === 0}
							aria-label={`Notifications (${notifications.length} unread)`}
							onClick={toggleNotification}
						>
							<BadgeContainer className="notification-badge-container">
								<img
									src={"/icon/notification.svg"}
									alt="notification"
									className="account-icon"
								/>
								<Badge
									className=""
									align={{ horizontal: "start", vertical: "bottom" }}
									cutoutBorder={true}
									position="inside"
									rounded="full"
									size="small"
									themeColor="error"
									style={{
										display: notifications.length > 0 ? "block" : "none",
									}}
								>
									{notifications.length}
								</Badge>
							</BadgeContainer>
						</Button>
					</div>
					<Popup
						anchor={notificationAnchorRef.current}
						anchorAlign={{
							horizontal: "right",
							vertical: "bottom",
						}}
						popupAlign={{
							horizontal: "right",
							vertical: "top",
						}}
						show={showNotification}
						popupClass={"notification-popup"}
						style={{
							borderRadius: "12px",
							background: "none",
							zIndex: 1300,
						}}
						margin={{ horizontal: -49, vertical: 0 }}
					>
						<div
							className="notification-popover-content"
							onClick={(event) => event.stopPropagation()}
							onMouseDown={(event) => event.stopPropagation()}
						>
							<Notification notifications={notifications} />
						</div>
					</Popup>
				</div>
				<div className="app-header-user-wrap" style={{}}>
					<div>
						<div ref={settingRef} className="header-popover-anchor">
							<div ref={settingAnchorRef}>
								<Button
									style={{
										backgroundColor: "transparent",
										borderColor: "transparent",
										fontFamily: "Work Sans",
										lineHeight: "18px",
										borderRadius: "12px",
										marginRight: "0px",
									}}
									onMouseOver={() => {
										setShowSetting(true);
										setShowOrganizations(false);
										setShowNotification(false);
									}}
									className="user-setting-btn"
								>
									<img
										src={"/icon/user.svg"}
										alt="account"
										className="account-icon"
									/>
								</Button>
							</div>
							<Popup
								anchor={settingAnchorRef.current}
								appendTo={settingRef.current}
								anchorAlign={{
									horizontal: "right",
									vertical: "bottom",
								}}
								popupAlign={{
									horizontal: "right",
									vertical: "top",
								}}
								show={showSetting}
								popupClass={"user-setting-popup"}
								style={{
									borderRadius: "12px",
									background: "none",
								}}
							>
								<UserSetting onSelect={() => setShowSetting(false)} />
							</Popup>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HeaderItem;
